/**
 * CijfersPage — bedrijfscijfers (/cijfers, admin-only).
 *
 * Vervangt de handmatige kwartaal-Excel: big picture op tab Overzicht,
 * inzoomen per domein op Wasserij en Klantcontact (incl. telefonie).
 * Data komt uit GET /api/kpi/overview en /api/kpi/series (kpi_facts:
 * live-rollup + Excel-historie); zes jaar terugkijken per grafiek.
 * Telefonie-KPI's lopen pas sinds juli 2026 — die grafieken beginnen daar.
 */

import { useEffect, useMemo, useState } from 'react';
import { DailyOmzetChart } from '../components/cijfers/DailyOmzetChart';
import { PeriodeDoelCard } from '../components/cijfers/PeriodeDoelCard';
import { DimensionSplit } from '../components/cijfers/DimensionSplit';
import { KpiCard } from '../components/cijfers/KpiCard';
import { KpiTrendChart } from '../components/cijfers/KpiTrendChart';
import { PeriodSwitcher } from '../components/cijfers/PeriodSwitcher';
import {
  useCijfersStore,
  type OverviewMetric,
  type Series,
} from '../stores/cijfers-store';

type TabId = 'overzicht' | 'wasserij' | 'klantcontact';

const TABS: Array<{ id: TabId; label: string }> = [
  { id: 'overzicht', label: 'Overzicht' },
  { id: 'wasserij', label: 'Wasserij' },
  { id: 'klantcontact', label: 'Klantcontact' },
];

const OVERZICHT_KEYS = [
  'omzet_uitvoering_wasserij',
  'm2_afgeleverd',
  'prijs_per_m2',
  'omzet_per_uur',
  'cheerleader_pct',
  'klachten_pct',
];

const WASSERIJ_GROUPS: Array<{ title: string; keys: string[] }> = [
  { title: 'Volume', keys: ['m2_gewassen', 'm2_afgeleverd', 'pct_particulier'] },
  { title: 'Commercieel', keys: ['omzet_uitvoering_wasserij', 'opdrachten', 'prijs_per_m2', 'omzet_per_opdracht'] },
  { title: 'Kwaliteit', keys: ['cheerleaders', 'cheerleader_pct', 'klachten', 'klachten_pct', 'refunds_aantal', 'refunds_pct', 'refunds_bedrag'] },
  { title: 'Capaciteit', keys: ['uren_totaal', 'uren', 'omzet_per_uur', 'fte', 'omzet_per_fte'] },
  { title: 'Routes', keys: ['routes_aantal', 'uur_per_route', 'omzet_per_route'] },
];

const KLANTCONTACT_GROUPS: Array<{ title: string; keys: string[] }> = [
  { title: 'Bezetting', keys: ['kc_uren', 'kc_fte', 'kc_omzet_verkoop_per_uur', 'kc_routes_per_fte'] },
  { title: 'Telefonie (sinds juli 2026)', keys: ['calls_inbound', 'calls_beantwoord_pct', 'voicemails', 'opvolgtaken_binnen_24u_pct'] },
];

/** Metric uit de overview halen; totaal-rij (dimension='') heeft voorrang. */
function pickMetric(domains: Record<string, OverviewMetric[]> | null, key: string): OverviewMetric | null {
  if (!domains) return null;
  for (const list of Object.values(domains)) {
    const exact = list.find((m) => m.key === key && m.dimension === '');
    if (exact) return exact;
  }
  return null;
}

/** Grote detailgrafiek voor de geselecteerde metric (+ dimensie-splitsing). */
function MetricDetail({ metricKey }: { metricKey: string }) {
  const loadSeries = useCijfersStore((s) => s.loadSeries);
  const grain = useCijfersStore((s) => s.grain);
  const [series, setSeries] = useState<Series | null>(null);

  useEffect(() => {
    let active = true;
    setSeries(null);
    void loadSeries(metricKey).then((s) => {
      if (active) setSeries(s);
    });
    return () => {
      active = false;
    };
  }, [metricKey, grain, loadSeries]);

  if (!series) {
    return <div className="h-72 flex items-center justify-center text-sm text-gray-400">Grafiek laden…</div>;
  }

  const hasDimensions = series.points.some((p) => p.dimension !== '');
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-sm font-medium text-main mb-2">{series.label}</h3>
        <KpiTrendChart series={series} grain={grain} />
      </div>
      {hasDimensions && (
        <div>
          <h3 className="text-sm font-medium text-main mb-2">Verdeling</h3>
          <DimensionSplit series={series} grain={grain} />
        </div>
      )}
    </div>
  );
}

function CardGrid({
  keys,
  selected,
  onSelect,
}: {
  keys: string[];
  selected: string | null;
  onSelect: (key: string) => void;
}) {
  const overview = useCijfersStore((s) => s.overview);
  const metrics = useMemo(
    () => keys.map((k) => pickMetric(overview, k)).filter((m): m is OverviewMetric => m !== null),
    [keys, overview]
  );
  if (metrics.length === 0) {
    return <p className="text-sm text-gray-400">Geen data voor deze periode.</p>;
  }
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
      {metrics.map((m) => (
        <KpiCard key={m.key} metric={m} selected={selected === m.key} onSelect={onSelect} />
      ))}
    </div>
  );
}

/** Overzicht: omzet verkoop vs uitvoering in één beeld + m² + kern-kaarten. */
function OverzichtTab({ selected, onSelect }: { selected: string | null; onSelect: (k: string) => void }) {
  const loadSeries = useCijfersStore((s) => s.loadSeries);
  const grain = useCijfersStore((s) => s.grain);
  const [verkoop, setVerkoop] = useState<Series | null>(null);
  const [uitvoering, setUitvoering] = useState<Series | null>(null);
  const [m2, setM2] = useState<Series | null>(null);

  useEffect(() => {
    let active = true;
    void loadSeries('omzet_verkoop').then((s) => active && setVerkoop(s));
    void loadSeries('omzet_uitvoering').then((s) => active && setUitvoering(s));
    void loadSeries('m2_afgeleverd').then((s) => active && setM2(s));
    return () => {
      active = false;
    };
  }, [grain, loadSeries]);

  return (
    <div className="space-y-8">
      {/* Volgt de grain van de switcher, maar altijd de LOPENDE periode. */}
      <PeriodeDoelCard />
      <DailyOmzetChart />

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-sm font-medium text-main mb-2">Omzet verkoop</h3>
          {verkoop ? (
            <KpiTrendChart series={verkoop} grain={grain} height={220} />
          ) : (
            <div className="h-56 flex items-center justify-center text-sm text-gray-400">Laden…</div>
          )}
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-sm font-medium text-main mb-2">Omzet uitvoering</h3>
          {uitvoering ? (
            <KpiTrendChart series={uitvoering} grain={grain} height={220} />
          ) : (
            <div className="h-56 flex items-center justify-center text-sm text-gray-400">Laden…</div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h3 className="text-sm font-medium text-main mb-2">m² afgeleverd</h3>
        {m2 ? (
          <KpiTrendChart series={m2} grain={grain} height={220} />
        ) : (
          <div className="h-56 flex items-center justify-center text-sm text-gray-400">Laden…</div>
        )}
      </div>

      <div>
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Kern-KPI's</h2>
        <CardGrid keys={OVERZICHT_KEYS} selected={selected} onSelect={onSelect} />
      </div>

      {selected && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <MetricDetail metricKey={selected} />
        </div>
      )}
    </div>
  );
}

function GroupedTab({
  groups,
  selected,
  onSelect,
  footer,
}: {
  groups: Array<{ title: string; keys: string[] }>;
  selected: string | null;
  onSelect: (k: string) => void;
  footer?: React.ReactNode;
}) {
  return (
    <div className="space-y-8">
      {groups.map((group) => (
        <div key={group.title}>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">{group.title}</h2>
          <CardGrid keys={group.keys} selected={selected} onSelect={onSelect} />
          {/* Detailgrafiek direct onder de groep van de aangeklikte kaart,
              niet onderaan de pagina (daar valt hij buiten beeld). */}
          {selected && group.keys.includes(selected) && (
            <div className="bg-white rounded-xl border border-gray-200 p-4 mt-3">
              <MetricDetail metricKey={selected} />
            </div>
          )}
        </div>
      ))}
      {footer}
    </div>
  );
}

/** Belredenen-verdeling: maand/kwartaal-lens op de belredenen-taxonomie. */
function BelredenenBlok() {
  const loadSeries = useCijfersStore((s) => s.loadSeries);
  const grain = useCijfersStore((s) => s.grain);
  const [series, setSeries] = useState<Series | null>(null);

  useEffect(() => {
    let active = true;
    void loadSeries('calls_per_categorie').then((s) => active && setSeries(s));
    return () => {
      active = false;
    };
  }, [grain, loadSeries]);

  if (!series || series.points.length === 0) return null;
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <h3 className="text-sm font-medium text-main mb-2">Gesprekken per belreden</h3>
      <DimensionSplit series={series} grain={grain} />
    </div>
  );
}

export function CijfersPage() {
  const { overview, overviewLoading, overviewError, loadOverview } = useCijfersStore();
  const [tab, setTab] = useState<TabId>('overzicht');
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    if (!overview) void loadOverview();
  }, [overview, loadOverview]);

  const handleSelect = (key: string) => setSelected((cur) => (cur === key ? null : key));

  return (
    <>
      <div className="flex-1 overflow-hidden flex flex-col bg-muted">
        <div className="px-8 py-6 border-b border-gray-200 bg-white shadow-sm">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <h1 className="text-2xl font-heading font-semibold text-main">Cijfers</h1>
            <PeriodSwitcher />
          </div>
          <div className="flex gap-1 mt-4 -mb-6">
            {TABS.map(({ id, label }) => (
              <button
                key={id}
                onClick={() => {
                  setTab(id);
                  setSelected(null);
                }}
                className={`px-4 py-2.5 text-sm border-b-2 transition-colors ${
                  tab === id
                    ? 'border-accent text-main font-medium'
                    : 'border-transparent text-gray-500 hover:text-main'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-8 py-6">
          {overviewError && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 text-sm text-red-700">
              Cijfers laden mislukt: {overviewError}
            </div>
          )}
          {overviewLoading && !overview && (
            <p className="text-sm text-gray-400">Cijfers laden…</p>
          )}

          {tab === 'overzicht' && <OverzichtTab selected={selected} onSelect={handleSelect} />}
          {tab === 'wasserij' && (
            <GroupedTab groups={WASSERIJ_GROUPS} selected={selected} onSelect={handleSelect} />
          )}
          {tab === 'klantcontact' && (
            <GroupedTab
              groups={KLANTCONTACT_GROUPS}
              selected={selected}
              onSelect={handleSelect}
              footer={<BelredenenBlok />}
            />
          )}
        </div>
      </div>
    </>
  );
}
